import React, { useState } from 'react';
import styled from 'styled-components';

interface FilterOptions {
  status: string[];
  tags: string[];
}

interface MonitorsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterOptions) => void;
  activeFilters: FilterOptions;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid #FB6F2A;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #111827;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  color: #111827;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #d1d5db;
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #FB6F2A;
  }

  span {
    color: #374151;
    font-size: 14px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  color: #111827;
  font-size: 14px;
  transition: border-color 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #FB6F2A;
    box-shadow: 0 0 0 3px rgba(251, 111, 42, 0.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(270deg, #D33324 0%, #FB6F2A 100%);
    color: white;
    box-shadow: 0 2px 4px rgba(251, 111, 42, 0.2);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(251, 111, 42, 0.3);
    }
  ` : `
    background-color: transparent;
    color: #6b7280;
    
    &:hover {
      color: #111827;
      background-color: #f3f4f6;
    }
  `}
`;

const statusOptions = [
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
  { value: 'expiring-domain', label: 'Expiring domain' },
  { value: 'expiring-ssl', label: 'Expiring SSL certificate' },
  { value: 'paused', label: 'Paused' },
  { value: 'not-started', label: 'Not started' },
];

const MonitorsFilterModal: React.FC<MonitorsFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  activeFilters,
}) => {
  const [filters, setFilters] = useState<FilterOptions>(activeFilters);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const handleStatusChange = (value: string) => {
    setFilters(prev => {
      const newStatus = prev.status.includes(value)
        ? prev.status.filter(s => s !== value)
        : [...prev.status, value];
      return { ...prev, status: newStatus };
    });
  };

  const handleReset = () => {
    setFilters({ status: [], tags: [] });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const getStatusLabel = () => {
    if (filters.status?.length === 0) return 'None';
    if (filters.status?.length === 1) {
      return statusOptions.find(opt => opt.value === filters.status[0])?.label || '';
    }
    return filters.status.map(s => 
      statusOptions.find(opt => opt.value === s)?.label
    ).join(', ');
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Filter</Title>
          <CloseButton onClick={onClose}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </CloseButton>
        </Header>

        <Content>
          <Section>
            <SectionTitle>Status</SectionTitle>
            <Dropdown>
              <DropdownButton 
                $isOpen={isStatusOpen}
                onClick={() => setIsStatusOpen(!isStatusOpen)}
              >
                <span>{getStatusLabel()}</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </DropdownButton>
              <DropdownMenu $isOpen={isStatusOpen}>
                {statusOptions.map(option => (
                  <CheckboxOption key={option.value}>
                    <input
                      type="checkbox"
                      checked={filters.status.includes(option.value)}
                      onChange={() => handleStatusChange(option.value)}
                    />
                    <span>{option.label}</span>
                  </CheckboxOption>
                ))}
              </DropdownMenu>
            </Dropdown>
          </Section>

          <Section>
            <SectionTitle>Tags</SectionTitle>
            <SearchInput
              placeholder="Search for a tag..."
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
            />
            
            <EmptyState>
              <EmptyStateIcon>😱</EmptyStateIcon>
              <EmptyStateTitle>You don't have any tags yet.</EmptyStateTitle>
              <EmptyStateText>
                To filter monitors based on tags create and attach tag to some monitor.
              </EmptyStateText>
            </EmptyState>
          </Section>
        </Content>

        <Footer>
          <Button $variant="secondary" onClick={handleReset}>
            Reset
          </Button>
          <Button $variant="primary" onClick={handleApply}>
            Apply
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default MonitorsFilterModal;

